/**
 * Fills the authenticated content area inside AppLayout main.
 * Use `flow` for scrollable pages; default keeps flex height for centered empty states.
 */
export function PageShell({ children, className = "", flow = false }) {
    return (
        <div className={`page-shell w-full ${flow ? "" : "flex min-h-0 flex-1 flex-col"} ${className}`}>
            {children}
        </div>
    );
}

/**
 * Centers content in a bounded flex region without flex/grid feedback loops.
 * Outer box claims flex space; inner slot is absolute + grid centered.
 */
export function ContentCenter({ children, testId }) {
    return (
        <div className="content-center" data-testid={testId}>
            <div className="content-center__slot">{children}</div>
        </div>
    );
}

/**
 * Empty state: centered surface card inside the full content area.
 */
export function EmptyStateWrapper({ children, testId, cardClassName = "" }) {
    return (
        <PageShell>
            <ContentCenter testId={testId}>
                <div className={`surface w-full max-w-lg p-10 text-center ${cardClassName}`}>
                    {children}
                </div>
            </ContentCenter>
        </PageShell>
    );
}

/**
 * Inline empty state card for use inside ShellContentFrame (no extra PageShell).
 * Pass `inline` when the parent already handles viewport centering (e.g. dashboard empty layer).
 */
export function EmptyStateCard({ children, testId, cardClassName = "", flow = false, inline = false }) {
    const card = (
        <div className={`surface w-full max-w-lg p-10 text-center ${cardClassName}`}>
            {children}
        </div>
    );
    if (inline) {
        return (
            <div data-testid={testId}>
                {card}
            </div>
        );
    }
    if (flow) {
        return (
            <div className="empty-state-viewport" data-testid={testId}>
                {card}
            </div>
        );
    }
    return <ContentCenter testId={testId}>{card}</ContentCenter>;
}

/**
 * Page with a fixed header row and centered content below (e.g. Pools list empty).
 */
export function PageWithHeader({ header, children, testId }) {
    return (
        <PageShell>
            <div className="shrink-0">{header}</div>
            <div className="relative min-h-[min(24rem,calc(100vh-14rem))] flex-1">
                <ContentCenter testId={testId}>{children}</ContentCenter>
            </div>
        </PageShell>
    );
}
